import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  Receipt,
  Download,
  Loader2,
  Truck,
  Layers,
  ShoppingBag,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";

export const SupplierExpensesReportModal = ({
  open,
  onOpenChange,
  supplier,
  storeId,
}) => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";
  const [downloading, setDownloading] = useState(false);

  const supplierId = supplier?.id;
  const supplierName =
    supplier?.company_name || supplier?.name || "Supplier";

  // 1. Fetch supplierwise expenses report summary
  const { data: reportData } = useQuery({
    enabled: Boolean(open && supplierId),
    queryKey: ["supplier-expense-report-modal", supplierId, storeId],
    queryFn: async () => {
      const params = {
        supplier_id: String(supplierId),
      };
      if (storeId && storeId !== "all") {
        params.store_id = storeId;
      }
      const res = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.SUPPLIERWISE_EXPENSES,
        { params },
      );
      return res.data;
    },
  });

  // 2. Fetch recent expenses for this supplier
  const { data: expensesData, isLoading: isLoadingExpenses } = useQuery({
    enabled: Boolean(open && supplierId),
    queryKey: ["supplier-expenses-list-modal", supplierId, storeId],
    queryFn: async () => {
      const params = {
        supplier_id: String(supplierId),
        per_page: 25,
        sort_by: "created_at",
        sort_order: "desc",
      };
      if (storeId && storeId !== "all") {
        params.store_id = storeId;
      }
      const res = await apiClient.get(`${API_BASE_URL}/expenses`, { params });
      return res.data;
    },
  });

  const stats = React.useMemo(() => {
    const rawList = reportData?.data || [];
    if (!Array.isArray(rawList)) return null;
    const match =
      rawList.find((r) => String(r.supplier_id) === String(supplierId)) ||
      rawList[0];
    return match || null;
  }, [reportData, supplierId]);

  const summary = reportData?.summary || null;

  const expenses = React.useMemo(() => {
    return expensesData?.data || [];
  }, [expensesData]);

  // Calculated totals
  const totalAmount =
    stats?.total_amount !== undefined
      ? parseFloat(stats.total_amount) || 0
      : summary?.total_amount !== undefined
        ? parseFloat(summary.total_amount) || 0
        : expenses.reduce((sum, e) => sum + (parseFloat(e.amount || e.total_amount) || 0), 0);

  const expenseTotal =
    stats?.expense_total !== undefined
      ? parseFloat(stats.expense_total) || 0
      : summary?.total_expenses !== undefined
        ? parseFloat(summary.total_expenses) || 0
        : totalAmount;

  const expenseCount =
    stats?.expense_count !== undefined
      ? parseInt(stats.expense_count) || 0
      : summary?.total_count !== undefined
        ? parseInt(summary.total_count) || 0
        : expenses.length;

  const poTotal =
    stats?.po_total !== undefined
      ? parseFloat(stats.po_total) || 0
      : summary?.total_pos !== undefined
        ? parseFloat(summary.total_pos) || 0
        : 0;

  const poCount =
    stats?.po_count !== undefined
      ? parseInt(stats.po_count) || 0
      : 0;

  const handleDownloadPDF = async () => {
    if (!supplierId) return;
    try {
      setDownloading(true);
      toast.info(`Generating expense report PDF for ${supplierName}...`);
      const params = {
        supplier_id: String(supplierId),
      };
      if (storeId && storeId !== "all") {
        params.store_id = storeId;
      }
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SUPPLIERWISE_EXPENSES,
        {
          params,
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `supplier-expense-report-${supplierName.replace(/\s+/g, "_")}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Expense report PDF downloaded successfully!");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to download expense report PDF",
      );
    } finally {
      setDownloading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "paid" || s === "completed") return "success";
    if (s === "submitted" || s === "pending") return "warning";
    if (s === "rejected" || s === "cancelled") return "destructive";
    return "secondary";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b bg-muted/20">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <Truck className="h-5 w-5 text-primary" />
                  {supplierName}
                </DialogTitle>
                {supplier?.status && (
                  <Badge
                    variant={supplier.status === "active" ? "success" : "secondary"}
                    className="capitalize"
                  >
                    {supplier.status}
                  </Badge>
                )}
              </div>
              <DialogDescription className="mt-1 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                {supplier?.email && <span>Email: {supplier.email}</span>}
                {supplier?.phone && <span>Phone: {supplier.phone}</span>}
                {supplier?.city && <span>City: {supplier.city}</span>}
                {supplier?.country && <span>Country: {supplier.country}</span>}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="rounded-lg shadow-sm"
              >
                {downloading ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-1.5 h-4 w-4" /> Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Body content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="border shadow-sm">
              <CardContent className="p-3.5 flex flex-col justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  Total Amount
                </p>
                <h3 className="text-xl font-bold text-primary mt-1">
                  {currencySymbol}
                  {totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </h3>
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardContent className="p-3.5 flex flex-col justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  Expense Total
                </p>
                <h3 className="text-xl font-bold text-foreground mt-1">
                  {currencySymbol}
                  {expenseTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </h3>
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardContent className="p-3.5 flex flex-col justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  Expense Count
                </p>
                <h3 className="text-xl font-bold text-foreground mt-1">
                  {expenseCount}
                </h3>
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardContent className="p-3.5 flex flex-col justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  PO Total
                </p>
                <h3 className="text-xl font-bold text-blue-600 mt-1">
                  {currencySymbol}
                  {poTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </h3>
              </CardContent>
            </Card>

            <Card className="border shadow-sm col-span-2 md:col-span-1">
              <CardContent className="p-3.5 flex flex-col justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  PO Count
                </p>
                <h3 className="text-xl font-bold text-foreground mt-1">
                  {poCount}
                </h3>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Expenses Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold tracking-tight text-foreground flex items-center gap-2">
                <Receipt className="h-4 w-4 text-primary" /> Recent Expenses
              </h4>
              <span className="text-xs text-muted-foreground">
                Showing {expenses.length} records
              </span>
            </div>

            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-semibold text-xs">
                      Reference / Title
                    </TableHead>
                    <TableHead className="font-semibold text-xs">
                      Date
                    </TableHead>
                    <TableHead className="font-semibold text-xs">
                      Category
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-right">
                      Amount
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-center">
                      Payment Method
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-center">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingExpenses ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-28 text-center">
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <span>Loading expenses...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : expenses.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-24 text-center text-muted-foreground text-sm"
                      >
                        No expense records found for this supplier.
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses.map((expense) => {
                      const amount =
                        parseFloat(expense.total_amount || expense.amount) || 0;
                      return (
                        <TableRow
                          key={expense.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <TableCell className="font-medium text-xs">
                            <div>
                              <p className="text-foreground">
                                {expense.title || expense.reference_number || `#${expense.id}`}
                              </p>
                              {expense.receipt_number && (
                                <p className="text-[11px] text-muted-foreground">
                                  Receipt: {expense.receipt_number}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {formatDate(expense.expense_date || expense.created_at)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {expense.category?.name || expense.category_name || "-"}
                          </TableCell>
                          <TableCell className="text-xs text-right font-semibold text-foreground">
                            {currencySymbol}
                            {amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell className="text-center text-xs text-muted-foreground capitalize">
                            {expense.payment_method?.name ||
                              expense.payment_method ||
                              "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={getStatusBadge(expense.status)}
                              className="text-[10px] capitalize px-2 py-0"
                            >
                              {expense.status || "Draft"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="p-4 border-t bg-muted/10 flex justify-between sm:justify-between items-center">
          <div className="text-xs text-muted-foreground">
            Total records: {expenses.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadPDF}
              disabled={downloading}
            >
              <Download className="mr-1.5 h-4 w-4" /> Download Report PDF
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierExpensesReportModal;
